import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import { Link } from "react-router-dom";
import type { ListingHealthDto } from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { LinkGen, QueryKeys } from "../LinkGen";

function locationLabel(location: ListingHealthDto, index: number) {
	return (
		location.publicName?.trim() ||
		location.googleLocationId ||
		`Location ${index + 1}`
	);
}

export const ListingHealthCard: FC = () => {
	const { id: companyId } = useCompany();
	const { data, isLoading } = useQuery({
		queryKey: QueryKeys.Locations.ListingHealth(companyId),
		queryFn: async () => {
			const response = await Api.LocationsListListingHealth();
			return response.data;
		},
		staleTime: 60_000,
	});

	const score = data?.averageScore ?? null;
	const locations = data?.locations ?? [];

	return (
		<Card className="stat">
			<Card.Header>Listing Health</Card.Header>
			<Card.Body>
				<div
					style={{
						display: "flex",
						alignItems: "baseline",
						justifyContent: "space-between",
						gap: 16,
						flexWrap: "wrap",
					}}
				>
					<div>
						<div
							style={{
								fontSize: 36,
								fontWeight: 700,
								lineHeight: 1.1,
								color: "var(--color-navy)",
							}}
						>
							{isLoading || score == null ? "—" : `${score}`}
							{score != null ? (
								<span
									style={{
										fontSize: 16,
										fontWeight: 500,
										color: "var(--bs-secondary-color)",
										marginLeft: 4,
									}}
								>
									/100
								</span>
							) : null}
						</div>
						<p
							style={{
								margin: "6px 0 0",
								fontSize: 13,
								color: "var(--bs-secondary-color)",
							}}
						>
							{locations.length > 1
								? `Average across ${locations.length} locations`
								: locations.length === 1
									? locationLabel(locations[0], 0)
									: "Platforms claimed, profile fields, drift, reply rate, review recency"}
						</p>
					</div>
					<Link to={LinkGen.Google.MyBusiness.Tab("profile")}>
						Open Business Manager
					</Link>
				</div>

				{locations.length > 0 ? (
					<div
						style={{
							display: "grid",
							gap: 10,
							marginTop: 16,
						}}
					>
						{locations.map((location, index) => (
							<div
								key={location.locationId}
								style={{
									padding: "10px 12px",
									border: "1px solid var(--bs-border-color)",
									borderRadius: 8,
								}}
							>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "baseline",
										gap: 12,
										marginBottom: 6,
									}}
								>
									<span style={{ fontWeight: 600, fontSize: 14 }}>
										{locationLabel(location, index)}
									</span>
									<span
										style={{
											fontWeight: 700,
											fontSize: 14,
											color: "var(--color-navy)",
											whiteSpace: "nowrap",
										}}
									>
										{location.score}/100
									</span>
								</div>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
										gap: 6,
										fontSize: 12,
										color: "var(--bs-secondary-color)",
									}}
								>
									<span>
										Platforms {location.breakdown.platformsClaimed}/20
									</span>
									<span>Fields {location.breakdown.fieldsComplete}/20</span>
									<span>Drift {location.breakdown.drift}/20</span>
									<span>Replies {location.breakdown.replyRate}/20</span>
									<span>Recency {location.breakdown.reviewRecency}/20</span>
								</div>
							</div>
						))}
					</div>
				) : null}
			</Card.Body>
		</Card>
	);
};
